import { SCHEDULE_DAYS, SCHEDULE_LIMITS, type ScheduleDay } from "@sagot/constants";
import type { CustomSchedule, TimeRange } from "../types";

const WEEKDAYS: readonly ScheduleDay[] = ["mon", "tue", "wed", "thu", "fri"];
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Monday to Friday, 8 AM to 5 PM: the starting point when a workflow switches to a custom schedule. */
export function defaultCustomSchedule(): CustomSchedule {
  return { days: Object.fromEntries(WEEKDAYS.map((day) => [day, [{ from: SCHEDULE_LIMITS.defaultFrom, to: SCHEDULE_LIMITS.defaultTo }]])) };
}

export const rangesOf = (schedule: CustomSchedule, day: ScheduleDay): TimeRange[] => schedule.days[day] ?? [];

function withDay(schedule: CustomSchedule, day: ScheduleDay, ranges: TimeRange[]): CustomSchedule {
  const days = Object.fromEntries(SCHEDULE_DAYS.flatMap((key) => {
    const list = key === day ? ranges : rangesOf(schedule, key);
    return list.length > 0 ? [[key, list]] : [];
  }));
  return { ...schedule, days };
}

/** A new range after the last one of the day (or the default hours when the day was closed). */
export function addRange(schedule: CustomSchedule, day: ScheduleDay): CustomSchedule {
  const ranges = rangesOf(schedule, day);
  if (ranges.length >= SCHEDULE_LIMITS.rangesPerDay) return schedule;
  const last = ranges.at(-1);
  const next: TimeRange = last && last.to < "23:00" ? { from: last.to, to: "23:00" } : { from: SCHEDULE_LIMITS.defaultFrom, to: SCHEDULE_LIMITS.defaultTo };
  return withDay(schedule, day, [...ranges, next]);
}

export function updateRange(schedule: CustomSchedule, day: ScheduleDay, index: number, patch: Partial<TimeRange>): CustomSchedule {
  return withDay(schedule, day, rangesOf(schedule, day).map((range, i) => (i === index ? { ...range, ...patch } : range)));
}

export function removeRange(schedule: CustomSchedule, day: ScheduleDay, index: number): CustomSchedule {
  return withDay(schedule, day, rangesOf(schedule, day).filter((_, i) => i !== index));
}

export type RangeProblem = "format" | "order" | "overlap";

/** What is wrong with one range of a day (the range after an earlier one that it overlaps counts as the overlap). */
export function rangeProblem(ranges: readonly TimeRange[], index: number): RangeProblem | null {
  const range = ranges[index];
  if (!range) return null;
  if (!TIME.test(range.from) || !TIME.test(range.to)) return "format";
  if (range.from >= range.to) return "order";
  const overlaps = ranges.some((other, i) => i < index && TIME.test(other.from) && TIME.test(other.to) && other.from < range.to && range.from < other.to);
  return overlaps ? "overlap" : null;
}

/** Whether every range of the schedule is valid. */
export const isScheduleValid = (schedule: CustomSchedule): boolean => SCHEDULE_DAYS.every((day) => rangesOf(schedule, day).every((_, i) => rangeProblem(rangesOf(schedule, day), i) === null));
